import React, { useMemo } from "react";
import { useTable, usePagination, useSortBy, useFilters } from "react-table";
import { Card, CardBody, CardTitle, Table, Input, Button, Row, Col, Spinner, CardSubtitle } from "reactstrap";

const DataTable = ({ title, subTitle, isAddNew, tableData, columns, actionButtons, isLoading }) => {
  const data = useMemo(() => tableData, [tableData]);

  const tableColumns = useMemo(
    () => [
      {
        Header: "No.",
        id: "srNo",
        filter: false,
        Cell: ({ row }) => (row.index + 1) + pageIndex * 5,
      },
      ...columns.map((col) => ({
        ...col,
        Filter: col.filter !== false ? DefaultColumnFilter : () => null,
      })),
      {
        Header: "Actions",
        id: "actions",
        filter: false,
        Cell: ({ row }) => (
          <div className="d-flex">
            {actionButtons.map((action, index) => (
              <span
                key={index}
                className="me-2"
                style={{ cursor: "pointer" }}
                onClick={() => action.onClick(row.original)}
              >
                <i className={action?.icon + " " + `text-${action?.color ? action?.color : "text-secondary"}`}></i>
              </span>
            ))
            }
          </div >
        ),
      },
    ],
    [columns, actionButtons]
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns: tableColumns,
      data,
      defaultColumn,
      initialState: { pageSize: 5 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  return (
    <div>
      <Card>
        <CardBody>
          <Row>
            <Col>
              <CardTitle tag="h5">{title}</CardTitle>
              {subTitle && <CardSubtitle
              // tag="h5"
              >{subTitle}</CardSubtitle>}
            </Col>
            <Col className="text-end">
              {isAddNew && <Button color="primary" onClick={isAddNew}><i className="bi bi-plus"></i> Add New</Button>}
            </Col>
          </Row>

          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <Table {...getTableProps()} responsive borderless className="mt-3 align-middle">
                <thead>
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <th key={column.id}>
                          <div
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            style={{ cursor: 'pointer' }}
                          >
                            {column.render("Header")}
                            <span>
                              {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                            </span>
                          </div>
                          <div style={{ height: "38px" }}>{column.canFilter ? column.render("Filter") : null}</div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumns.length}>No data available</td>
                    </tr>
                  ) : (
                    page.map(row => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} className="border-top">
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between">
                <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
                  <i className="bi bi-arrow-left"></i> Previous
                </Button>
                <span>
                  Page {pageIndex + 1} of {pageOptions.length} | Rows {page.length} of {data.length}
                </span>
                <Button onClick={() => nextPage()} disabled={!canNextPage}>
                  Next <i className="bi bi-arrow-right"></i>
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <Input
      value={filterValue || ""}
      onChange={e => setFilter(e.target.value || undefined)}
      placeholder={`Search...`}
    />
  );
}

export default DataTable;
